/**
 * 麦克风测试弹窗（「设置」菜单里的「开启麦克风」）。
 *
 * 用途：视弹/跟弹靠麦克风拾音判弹得对不对，而浏览器授权麦克风、以及麦克风是否
 * 真能拾到声，是两件用户常搞不清的事。这个弹窗一次性把两件事都验证掉：
 *   1. 请求 `getUserMedia`（浏览器弹授权框）；
 *   2. 用 `AnalyserNode` 实时读电平，画出音量条 —— 有跳动说明拾音正常。
 *
 * 只做「检测/授权」，不进入识别判定（那是视弹面板里 `RealtimePolyphonicEngine`
 * 的职责），所以这里用最轻的 Web Audio 直连，不引 worker / 多音引擎。
 *
 * 样式对齐 AppDialog 的版式规范（backdrop / paper2 盒 / serif），深浅主题随 t.palette。
 */
import React, { useEffect, useRef, useState } from 'react'
import { BREAKPOINT, ThemeContextValue, uiPrimitives, useThemedStyles } from '../theme'
import { describeMicError } from '@sparks-notation/react-editor/inspector/practice/micError'

type State =
	| { kind: 'idle' }                                  // 还没请求
	| { kind: 'opening' }                               // 正在请求麦克风
	| { kind: 'ready' }                                 // 已授权，正在听电平
	| { kind: 'denied'; message: string }               // 授权失败 / 无设备

export function MicTestDialog(props: { onClose: () => void }) {
	const classes = useThemedStyles(stylesFactory)
	const [state, setState] = useState<State>({ kind: 'idle' })
	const [level, setLevel] = useState(0)               // 0~1 归一化电平
	const streamRef = useRef<MediaStream | null>(null)
	const rafRef = useRef(0)

	// 卸载：停流、停 rAF
	useEffect(() => () => {
		cancelAnimationFrame(rafRef.current)
		streamRef.current?.getTracks().forEach(t => t.stop())
	}, [])

	async function start() {
		setState({ kind: 'opening' })
		try {
			const stream = await navigator.mediaDevices.getUserMedia({
				audio: {
					echoCancellation: false,
					noiseSuppression: false,
					autoGainControl: false,
				},
			})
			streamRef.current = stream
			const ctx = new AudioContext()
			const src = ctx.createMediaStreamSource(stream)
			const analyser = ctx.createAnalyser()
			analyser.fftSize = 1024
			src.connect(analyser)
			setState({ kind: 'ready' })
			const buf = new Uint8Array(analyser.fftSize)
			const tick = () => {
				analyser.getByteTimeDomainData(buf)
				// RMS 归一化到 0~1：时域字节中值 128，峰值 255。
				let sum = 0
				for (let i = 0; i < buf.length; i++) {
					const v = (buf[i] - 128) / 128
					sum += v * v
				}
				setLevel(Math.min(1, Math.sqrt(sum / buf.length)))
				rafRef.current = requestAnimationFrame(tick)
			}
			tick()
		} catch (err: any) {
			setState({ kind: 'denied', message: describeMicError(err) })
		}
	}

	function close() {
		props.onClose()
	}

	const loud = level > 0.12   // 有明确声音的阈值

	return <div className={classes.backdrop} onClick={close}>
		<div className={classes.box} role="dialog" aria-modal="true" aria-label="开启麦克风" onClick={e => e.stopPropagation()}>
			<h3 className={classes.title}>开启麦克风</h3>

			{state.kind === 'idle' && <>
				<p className={classes.body}>
					视弹和跟弹靠麦克风判断你弹得对不对。点「测试麦克风」，浏览器会请求麦克风权限，随后对着麦克风说话或弹一下，看音量条是否跳动。
				</p>
				<div className={classes.acts}>
					<button className={classes.tb + ' ' + classes.tbOutline} onClick={close}>取消</button>
					<button className={classes.tb + ' ' + classes.tbPrimary} onClick={start}>测试麦克风</button>
				</div>
			</>}

			{state.kind === 'opening' && <p className={classes.body}>正在请求麦克风权限…</p>}

			{state.kind === 'ready' && <>
				<p className={classes.body}>
					麦克风已连接。对着麦克风<b>说话或弹一个音</b>，下面的音量条会跟着跳动。
				</p>
				<div className={classes.meterTrack}>
					<div className={classes.meterFill} style={{ width: `${Math.round(level * 100)}%` }} />
				</div>
				<p className={loud ? classes.ok : classes.hint}>
					{loud ? '✓ 检测到声音，麦克风工作正常' : '音量条暂无跳动，请试着说话或弹琴'}
				</p>
				<div className={classes.acts}>
					<button className={classes.tb + ' ' + classes.tbPrimary} onClick={close}>完成</button>
				</div>
			</>}

			{state.kind === 'denied' && <>
				<p className={classes.err}>{state.message}</p>
				<div className={classes.acts}>
					<button className={classes.tb + ' ' + classes.tbOutline} onClick={close}>取消</button>
					<button className={classes.tb + ' ' + classes.tbPrimary} onClick={start}>重试</button>
				</div>
			</>}
		</div>
	</div>
}

const stylesFactory = (t: ThemeContextValue) => ({
	...uiPrimitives(t),

	backdrop: {
		position: 'fixed' as const,
		top: 0, right: 0, bottom: 0, left: 0,
		background: 'rgba(0,0,0,.42)',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		zIndex: 2000,
		padding: 16,
	},
	box: {
		background: t.palette.paper2,
		border: `1px solid ${t.palette.line}`,
		borderRadius: t.radius.lg,
		boxShadow: t.shadows.lg,
		padding: 22,
		width: 420,
		maxWidth: '100%',
		color: t.palette.ink,
		[`@media (max-width: ${BREAKPOINT}px)`]: {
			padding: 18,
		},
	},
	title: {
		fontFamily: t.fonts.serif,
		fontSize: 17,
		fontWeight: 700,
		margin: '0 0 10px',
	},
	body: {
		fontFamily: t.fonts.serif,
		fontSize: 14,
		lineHeight: 1.75,
		color: t.palette.ink2,
		margin: '0 0 18px',
	},
	err: {
		fontFamily: t.fonts.serif,
		fontSize: 14,
		lineHeight: 1.75,
		color: t.palette.cinnabar,
		margin: '0 0 18px',
	},
	meterTrack: {
		height: 12,
		borderRadius: 6,
		background: t.palette.paper3,
		border: `1px solid ${t.palette.line}`,
		overflow: 'hidden',
		marginBottom: 10,
	},
	meterFill: {
		height: '100%',
		borderRadius: 6,
		background: t.palette.celadonInk,
		transition: 'width .08s linear',
	},
	ok: {
		fontFamily: t.fonts.serif,
		fontSize: 13,
		color: t.palette.celadonInk,
		margin: '0 0 18px',
	},
	hint: {
		fontFamily: t.fonts.serif,
		fontSize: 13,
		color: t.palette.ink3,
		margin: '0 0 18px',
	},
	acts: {
		display: 'flex',
		gap: 8,
		justifyContent: 'flex-end',
	},
})
